/* eslint-disable camelcase */
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import inboxOutline from "@iconify/icons-eva/inbox-outline";
import calendarOutline from "@iconify/icons-eva/calendar-outline";
import downloadOutline from "@iconify/icons-eva/download-outline";
import trash2Outline from "@iconify/icons-eva/trash-2-outline";
import eyeOutline from "@iconify/icons-eva/eye-outline";
import closeFill from "@iconify/icons-eva/close-fill";
import refreshOutline from "@iconify/icons-eva/refresh-outline";
// material
import {
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Avatar,
  Button,
  Container,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Box,
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import searchFill from "@iconify/icons-eva/search-fill";
// components
import Page from "../../../../components/Page";
import ClientsListToolbar from "../components/FinancialListToolbar";

import financialService from "../services/financialService";
import AlertMessage from "../../../../components/alert/alert";
import FinancialDialog from "../components/FinancialDialog";

export default function Financial() {
  let currentRole = "";
  try {
    const stored = localStorage.getItem("user");
    currentRole = stored ? JSON.parse(stored)?.role || "" : "";
  } catch (error) {
    currentRole = "";
  }
  const canClose = currentRole === "Administrador" || currentRole === "Financeiro";

  const [page, setPage] = useState(0);
  const [data, setData]: any = useState([]);
  const [openOrders, setOpenOrders]: any = useState(null);
  const [showError, setError] = useState(false);
  const [showSuccess, setSuccess] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [dialogConfig, setDialogConfig] = useState<{
    open: boolean;
    isLoading: boolean;
    title: string;
    description: string;
    action: () => void;
  }>({
    open: false,
    isLoading: false,
    title: "",
    description: "",
    action: () => {},
  });
  const [notFirstQuery, setNotFirstQuery] = useState(false);
  const [queryStartEnd, setQueryStartEnd] = useState([
    "0",
    rowsPerPage.toString(),
  ]);

  const [detailsModal, setDetailsModal]: any = useState({
    open: false,
    loading: false,
    customers: [],
    selectedDetails: {},
    title: "",
    idBilling: "",
    branch: "TODAS",
    searchCustomer: null,
    searchOrder: "",
  });

  const [params, setParams]: any = useState({
    startDate: undefined,
    endDate: undefined,
  });

  const fetchFinancialList = async () => {
    try {
      const billingResponse = await financialService.getAll(
        queryStartEnd[0],
        queryStartEnd[1],
        params,
      );
      setData(billingResponse?.data || []);

      const openOrdersResponse = await financialService.getOpenOrdersTotals();
      setOpenOrders(openOrdersResponse?.data || null);
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleRefreshOpenOrders = () => {
    setRefreshLoading(true);
    financialService
      .getOpenOrdersTotals(true)
      .then((response: any) => setOpenOrders(response.data))
      .catch((error: any) => console.error(error))
      .finally(() => setRefreshLoading(false));
  };

  useEffect(() => {
    if (dialogConfig.isLoading) return;
    fetchFinancialList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogConfig.isLoading, queryStartEnd]);

  useEffect(() => {
    if (!showSuccess || dialogConfig.isLoading) return;
    fetchFinancialList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccess, dialogConfig.isLoading]);

  useEffect(() => {
    const timeOutId = setTimeout(
      () =>
        notFirstQuery
          ? financialService
              .getAll(queryStartEnd[0], queryStartEnd[1], params)
              .then((data: any) => setData(data.data))
              .catch((error: any) => console.error(error))
          : setNotFirstQuery(true),
      500,
    );

    return () => clearTimeout(timeOutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (showError) setTimeout(() => setError(false), 2000);
    if (showSuccess) setTimeout(() => setSuccess(false), 2000);
  }, [showError, showSuccess]);

  const handleOpenDetails = async (idBilling: string, title: string) => {
    setDetailsModal({
      ...detailsModal,
      open: true,
      loading: true,
      title,
      idBilling,
      customers: [],
      selectedDetails: {},
      branch: "TODAS",
      searchCustomer: null,
      searchOrder: "",
    });
    fetchDetailsCustomers(idBilling, "TODAS", "");
  };

  const fetchDetailsCustomers = async (
    idBilling: string,
    branch: string,
    orderCode?: string,
  ) => {
    setDetailsModal((prev: any) => ({ ...prev, loading: true }));
    try {
      const response = await financialService.getFinancialCustomers(
        idBilling,
        branch,
        orderCode,
      );
      const customers = response.data;

      setDetailsModal((prev: any) => ({
        ...prev,
        loading: false,
        customers,
        selectedDetails: {},
      }));

      // Se estamos pesquisando por OS, expandimos automaticamente os clientes retornados
      if (orderCode) {
        for (const cust of customers) {
          handleAccordionChange(cust.idCustomer_OS, orderCode);
        }
      }
    } catch (error) {
      console.error(error);
      setDetailsModal((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const handleAccordionChange = async (
    idCustomer: string,
    orderCodeOverride?: string,
  ) => {
    const oCode =
      orderCodeOverride !== undefined
        ? orderCodeOverride
        : detailsModal.searchOrder;

    // Se já temos os detalhes e o termo de busca não mudou, não fazemos nada
    if (detailsModal.selectedDetails[idCustomer] && !oCode) return;

    setDetailsModal((prev: any) => ({
      ...prev,
      selectedDetails: {
        ...prev.selectedDetails,
        [idCustomer]: { loading: true, data: {} },
      },
    }));

    try {
      const response = await financialService.getFinancialDetailsByCustomer(
        detailsModal.idBilling,
        idCustomer,
        oCode,
      );
      setDetailsModal((prev: any) => ({
        ...prev,
        selectedDetails: {
          ...prev.selectedDetails,
          [idCustomer]: { loading: false, data: response.data },
        },
      }));
    } catch (error) {
      console.error(error);
      setDetailsModal((prev: any) => ({
        ...prev,
        selectedDetails: {
          ...prev.selectedDetails,
          [idCustomer]: { loading: false, data: {} },
        },
      }));
    }
  };

  const handleSearchOrder = async () => {
    fetchDetailsCustomers(
      detailsModal.idBilling,
      detailsModal.branch,
      detailsModal.searchOrder,
    );
  };

  const handleClearFilters = () => {
    setDetailsModal((prev: any) => ({
      ...prev,
      branch: "TODAS",
      searchCustomer: null,
      searchOrder: "",
      selectedDetails: {},
    }));
    fetchDetailsCustomers(detailsModal.idBilling, "TODAS", "");
  };

  const handleChangePage = (event: any, newPage: any) => {
    const finalPage = newPage > 0 ? page + 1 : page - 1;
    setQueryStartEnd([
      ((finalPage + 1) * rowsPerPage - rowsPerPage).toString(),
      rowsPerPage.toString(),
    ]);
    setPage(finalPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const finalRowsPerPage = parseInt(event.target.value);
    setQueryStartEnd([
      ((page + 1) * finalRowsPerPage - finalRowsPerPage).toString(),
      finalRowsPerPage.toString(),
    ]);
    setRowsPerPage(finalRowsPerPage);
    setPage(0);
  };

  return (
    <Page title="Financeiro | Optools">
      <FinancialDialog
        open={dialogConfig.open}
        isLoading={dialogConfig.isLoading}
        title={dialogConfig.title}
        description={dialogConfig.description}
        confirmButtonText="Confirmar"
        cancelButtonText="Cancelar"
        action={dialogConfig.action}
        handler={() => setDialogConfig({ ...dialogConfig, open: false })}
      />

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h4" gutterBottom>
            Financeiro
          </Typography>
        </Stack>

        <Stack
          direction={["column", "row"]}
          justifyContent="space-between"
          alignItems="center"
        >
          <ClientsListToolbar
            onFilterDate={(startDate: string, endDate: string) => {
              setPage(0);
              setQueryStartEnd(["0", rowsPerPage.toString()]);
              setParams({
                startDate: startDate.length ? startDate : undefined,
                endDate: endDate.length ? endDate : undefined,
              });
            }}
          />

          <Stack direction="row" spacing={1} sx={{ mt: [0, 4.5] }}>
            <IconButton
              onClick={handleRefreshOpenOrders}
              disabled={refreshLoading}
              sx={{
                height: "48px",
                width: "48px",
                borderRadius: "50%",
                background: "#004A78",
                color: "#FFF",
                "&:hover": {
                  bgcolor: "#226C9A",
                },
                "&.Mui-disabled": {
                  background: "#909090",
                  color: "#ddd",
                },
              }}
            >
              <Icon
                icon={refreshOutline}
                className={refreshLoading ? "rotate-animation" : ""}
              />
            </IconButton>

            {canClose && (
              <Button
                sx={{
                  height: "48px",
                  borderRadius: "40px",
                  background: "#004A78",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#226C9A",
                  },
                }}
                variant="contained"
                onClick={() => {
                  setDialogConfig({
                    open: true,
                    isLoading: false,
                    title: "Gerar Fechamento",
                    description:
                      "Tem certeza que deseja gerar o fechamento financeiro?",
                    action: () => {
                      setDialogConfig((prev) => ({ ...prev, isLoading: true }));
                      financialService
                        .billingClose()
                        .then(() => {
                          setSuccess(true);
                          fetchFinancialList();
                        })
                        .catch(() => {
                          setError(true);
                        })
                        .finally(() => {
                          setDialogConfig((prev) => ({
                            ...prev,
                            open: false,
                            isLoading: false,
                          }));
                        });
                    },
                  });
                }}
                color="secondary"
              >
                Gerar Fechamento
              </Button>
            )}
          </Stack>
        </Stack>

        <TableContainer
          component={Paper}
          sx={{ borderRadius: "16px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "#F4F6F8" }}>
              <TableRow>
                <TableCell
                  sx={{ color: "#004A78", fontSize: "14px", fontWeight: 700 }}
                >
                  ID
                </TableCell>
                <TableCell
                  sx={{ color: "#004A78", fontSize: "14px", fontWeight: 700 }}
                >
                  Data
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#004A78", fontSize: "14px", fontWeight: 700 }}
                >
                  Total SP
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#004A78", fontSize: "14px", fontWeight: 700 }}
                >
                  Total PA
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#004A78", fontSize: "14px", fontWeight: 700 }}
                >
                  Total NE
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#004A78", fontSize: "14px", fontWeight: 700 }}
                >
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page === 0 && openOrders && (
                <TableRow
                  sx={{
                    backgroundColor: "#E6F7FF",
                    "&:hover": { backgroundColor: "#BAE7FF" },
                  }}
                >
                  <TableCell
                    sx={{ color: "#0050B3", fontSize: "13px", fontWeight: 700 }}
                  >
                    SIMULAÇÃO
                  </TableCell>
                  <TableCell
                    sx={{ color: "#0050B3", fontSize: "13px", fontWeight: 700 }}
                  >
                    Ped. em Aberto
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "#0050B3", fontSize: "13px", fontWeight: 700 }}
                  >
                    {Number(openOrders.total_SP).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "#0050B3", fontSize: "13px", fontWeight: 700 }}
                  >
                    {Number(openOrders.total_PA).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "#0050B3", fontSize: "13px", fontWeight: 700 }}
                  >
                    {Number(openOrders.total_NE).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton
                        sx={{
                          background: "#004A78",
                          color: "#FFF",
                          "&:hover": { bgcolor: "#115B89" },
                        }}
                        onClick={() =>
                          handleOpenDetails("0", "Simulação de Fechamento")
                        }
                      >
                        <Icon icon={eyeOutline} />
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#0050B3",
                          fontStyle: "italic",
                          alignSelf: "center",
                          ml: 1,
                        }}
                      >
                        Previsão de Faturamento
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {data.length ? (
                data.slice(0, rowsPerPage).map((row: any) => {
                  const {
                    id_Billing,
                    serverRegisterDate_Billing,
                    filepath_Billing,
                    canRollback,
                    total_SP,
                    total_PA,
                    total_NE,
                  } = row;

                  const formatCurrency = (value: any) => {
                    return Number(value).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    });
                  };

                  return (
                    <TableRow
                      key={id_Billing}
                      sx={{ "&:hover": { backgroundColor: "#F9FAFB" } }}
                    >
                      <TableCell
                        sx={{
                          color: "#004A78",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {id_Billing}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#004A78",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {serverRegisterDate_Billing}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "#004A78",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {formatCurrency(total_SP || 0)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "#004A78",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {formatCurrency(total_PA || 0)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "#004A78",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {formatCurrency(total_NE || 0)}
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            sx={{
                              background: "#004A78",
                              color: "#FFF",
                              "&:hover": { bgcolor: "#115B89" },
                            }}
                            onClick={() =>
                              handleOpenDetails(
                                id_Billing,
                                `Detalhes do Fechamento #${id_Billing}`,
                              )
                            }
                          >
                            <Icon icon={eyeOutline} />
                          </IconButton>
                          <IconButton
                            color="primary"
                            onClick={() => {
                              window.open(filepath_Billing, "_blank");
                            }}
                            sx={{
                              background: "#004A78",
                              color: "#FFF",
                              "&:hover": {
                                bgcolor: "#115B89",
                              },
                            }}
                          >
                            <Icon icon={downloadOutline} />
                          </IconButton>
                          {canClose && canRollback && (
                            <IconButton
                              onClick={() => {
                                setDialogConfig({
                                  open: true,
                                  isLoading: false,
                                  title: "Desfazer Fechamento",
                                  description: `Tem certeza que deseja desfazer o fechamento #${id_Billing}? Esta ação não pode ser revertida.`,
                                  action: () => {
                                    setDialogConfig((prev) => ({
                                      ...prev,
                                      isLoading: true,
                                    }));
                                    financialService
                                      .rollbackClose(id_Billing)
                                      .then(() => {
                                        setSuccess(true);
                                        fetchFinancialList();
                                      })
                                      .catch(() => setError(true))
                                      .finally(() => {
                                        setDialogConfig((prev) => ({
                                          ...prev,
                                          open: false,
                                          isLoading: false,
                                        }));
                                      });
                                  },
                                });
                              }}
                              sx={{
                                background: "#FF4842",
                                color: "#FFF",
                                "&:hover": {
                                  bgcolor: "#B72116",
                                },
                              }}
                            >
                              <Icon icon={trash2Outline} />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Stack
                      mt={4}
                      mb={4}
                      width="100%"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Icon icon={inboxOutline} width="56px" color="#909090" />
                      <Typography
                        mt={1}
                        variant="body1"
                        sx={{ color: "#909090" }}
                      >
                        Nenhum item encontrado...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          page={0}
          component="div"
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[8, 16, 24]}
          backIconButtonProps={{ disabled: !page }}
          labelRowsPerPage="Linhas por página:"
          nextIconButtonProps={{
            disabled:
              (page + 1) * rowsPerPage >= data[0]?.total_Billing ||
              data.length < rowsPerPage,
          }}
          labelDisplayedRows={(rowsInfo) => {
            const totalPages = Math.ceil(
              rowsInfo.count / rowsPerPage,
            ).toString();
            return (
              <>
                {`Página: ${page + 1}${
                  !params.startDate && !params.endDate
                    ? `/${totalPages === "0" ? "1" : totalPages}`
                    : ""
                }`}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {!params.startDate && !params.endDate
                  ? `Total: ${rowsInfo.count}`
                  : ""}
              </>
            );
          }}
          onRowsPerPageChange={handleChangeRowsPerPage}
          count={data?.length ? data[0].total_Billing : 0}
        />
        {showSuccess && (
          <AlertMessage open={true} message="Sucesso ao gerar fechamento!" />
        )}
        {showError && (
          <AlertMessage open={true} message="Erro ao gerar fechamento" />
        )}

        <Dialog
          open={detailsModal.open}
          onClose={() => setDetailsModal({ ...detailsModal, open: false })}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ backgroundColor: "#F4F6F8", p: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" sx={{ color: "#004A78" }}>
                {detailsModal.title}
              </Typography>
              <IconButton
                onClick={() =>
                  setDetailsModal({ ...detailsModal, open: false })
                }
              >
                <Icon icon={closeFill} />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, minHeight: "400px" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              mb={2}
              alignItems="center"
              sx={{ pt: 2 }}
            >
              <FormControl sx={{ minWidth: 100 }} size="small">
                <InputLabel>Filial</InputLabel>
                <Select
                  value={detailsModal.branch}
                  label="Filial"
                  onChange={(e) => {
                    const newBranch = e.target.value;
                    setDetailsModal({ ...detailsModal, branch: newBranch });
                    fetchDetailsCustomers(detailsModal.idBilling, newBranch);
                  }}
                >
                  <MenuItem value="TODAS">TODAS</MenuItem>
                  <MenuItem value="SP">SP</MenuItem>
                  <MenuItem value="PA">PA</MenuItem>
                  <MenuItem value="NE">NE</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                sx={{ flexGrow: 1, minWidth: 200 }}
                size="small"
                options={detailsModal.customers}
                getOptionLabel={(option: any) => option.name_Customer}
                value={detailsModal.searchCustomer}
                onChange={(event, newValue) => {
                  setDetailsModal({
                    ...detailsModal,
                    searchCustomer: newValue,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar Cliente" />
                )}
              />

              <TextField
                size="small"
                label="Buscar OS"
                placeholder="Código"
                sx={{ width: { xs: "100%", sm: 150 } }}
                value={detailsModal.searchOrder}
                onChange={(e) =>
                  setDetailsModal({
                    ...detailsModal,
                    searchOrder: e.target.value,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchOrder();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon={searchFill} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                onClick={handleSearchOrder}
                sx={{
                  height: "40px",
                  bgcolor: "#004A78",
                  "&:hover": { bgcolor: "#115B89" },
                }}
              >
                Buscar
              </Button>

              <Button
                variant="outlined"
                color="error"
                onClick={handleClearFilters}
                sx={{ height: "40px", whiteSpace: "nowrap" }}
              >
                Limpar Filtros
              </Button>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {detailsModal.loading ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: "300px" }}
              >
                <Typography>Carregando clientes...</Typography>
              </Stack>
            ) : detailsModal.customers.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: "300px" }}
              >
                <Typography color="textSecondary">
                  Nenhum cliente encontrado.
                </Typography>
              </Stack>
            ) : (
              detailsModal.customers
                .filter(
                  (cust: any) =>
                    !detailsModal.searchCustomer ||
                    cust.idCustomer_OS ===
                      detailsModal.searchCustomer.idCustomer_OS,
                )
                .map((cust: any) => {
                  const custDetails =
                    detailsModal.selectedDetails[cust.idCustomer_OS];

                  return (
                    <Accordion
                      key={cust.idCustomer_OS}
                      expanded={
                        !!detailsModal.selectedDetails[cust.idCustomer_OS]
                      }
                      onChange={(_, expanded) => {
                        if (!expanded) {
                          setDetailsModal((prev: any) => {
                            const newSelected = { ...prev.selectedDetails };
                            delete newSelected[cust.idCustomer_OS];
                            return { ...prev, selectedDetails: newSelected };
                          });
                        } else {
                          handleAccordionChange(cust.idCustomer_OS);
                        }
                      }}
                      sx={{
                        mb: 1,
                        border: "1px solid #E0E0E0",
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          sx={{ width: "100%", alignItems: "center" }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "#004A78",
                              flexGrow: 1,
                            }}
                          >
                            {cust.name_Customer}
                          </Typography>

                          <Stack direction="row" spacing={3} sx={{ pr: 2 }}>
                            <Box sx={{ textAlign: "right" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "text.secondary",
                                  lineHeight: 1,
                                }}
                              >
                                Qtd
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: "#004A78" }}
                              >
                                {cust.os_count}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: "right" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "text.secondary",
                                  lineHeight: 1,
                                }}
                              >
                                Total
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: "#2E7D32" }}
                              >
                                {(
                                  Number(cust.total_value || 0) / 100
                                ).toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: "right" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "text.secondary",
                                  lineHeight: 1,
                                }}
                              >
                                Preço Médio
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: "#0050B3" }}
                              >
                                {(
                                  Number(cust.total_value || 0) /
                                  (cust.os_count * 100)
                                ).toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <Divider />
                        {!custDetails || custDetails.loading ? (
                          <Stack p={4} alignItems="center">
                            <Typography variant="body2">
                              Carregando ordens...
                            </Typography>
                          </Stack>
                        ) : (
                          <Box sx={{ width: "100%" }}>
                            {Object.keys(custDetails.data).length === 0 ? (
                              <Stack p={4} alignItems="center">
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  Nenhuma ordem encontrada para este cliente ou
                                  código.
                                </Typography>
                              </Stack>
                            ) : (
                              Object.entries(custDetails.data).map(
                                ([branch, orders]: [string, any]) => (
                                  <Box key={branch} sx={{ mb: 2 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        backgroundColor: "#f4f4f4",
                                        px: 2,
                                        py: 1,
                                        fontWeight: 700,
                                        color:
                                          branch === "SP"
                                            ? "#004A78"
                                            : branch === "PA"
                                              ? "#B72116"
                                              : "#226C9A",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      FILIAL: {branch} ({orders.length} OS
                                      {orders.length > 1 ? "s" : ""})
                                    </Typography>
                                    <Table size="small">
                                      <TableHead
                                        sx={{ backgroundColor: "#fdfdfd" }}
                                      >
                                        <TableRow>
                                          <TableCell
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: "12px",
                                            }}
                                          >
                                            Código
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: "12px",
                                            }}
                                          >
                                            Lente
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: "12px",
                                            }}
                                          >
                                            Qtde
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: "12px",
                                            }}
                                          >
                                            Data
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: "12px",
                                            }}
                                            align="right"
                                          >
                                            Valor
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {orders.map((order: any) => (
                                          <TableRow key={order.id_Order} hover>
                                            <TableCell
                                              sx={{ fontSize: "12px" }}
                                            >
                                              {order.code_Order}
                                            </TableCell>
                                            <TableCell
                                              sx={{ fontSize: "12px" }}
                                            >
                                              {order.name_Lens}
                                            </TableCell>
                                            <TableCell
                                              sx={{ fontSize: "12px" }}
                                            >
                                              {order.lensesAmount_Order}
                                            </TableCell>
                                            <TableCell
                                              sx={{ fontSize: "12px" }}
                                            >
                                              {order.updated_at}
                                            </TableCell>
                                            <TableCell
                                              sx={{ fontSize: "12px" }}
                                              align="right"
                                            >
                                              {Number(
                                                order.valueWithDiscount ||
                                                  order.value_ServiceOrder,
                                              ).toLocaleString("pt-BR", {
                                                style: "currency",
                                                currency: "BRL",
                                              })}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                ),
                              )
                            )}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDetailsModal({ ...detailsModal, open: false })}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
